import React, { useState } from 'react';
import CoordPickerModal from '../harvesting/CoordPickerModal';

const CATEGORIES = [
  { value: 'cultural_heritage',  label: '🏛️ Cultural Heritage',   hint: 'Historic sites, cultural landmarks, sacred landmarks' },
  { value: 'ecological_zone',    label: '🌿 Ecological Zone',      hint: 'Habitat areas, river systems, ecological corridors' },
  { value: 'resource_area',      label: '🎣 Resource Area',        hint: 'Traditional harvesting territories, hunting/fishing areas' },
  { value: 'sacred_site',        label: '✨ Sacred Site',          hint: 'Spiritually or ceremonially significant places' },
  { value: 'seasonal_territory', label: '🍂 Seasonal Territory',   hint: 'Areas used by season — migration routes, gathering grounds' },
  { value: 'governance_area',    label: '🗺️ Governance Area',      hint: 'Treaty territories, land-management or jurisdictional zones' },
  { value: 'other',              label: '📍 Other',                hint: 'Any other land value not covered above' },
];

const VISIBILITY = [
  { value: 'community_only', label: 'Community only — visible to your community members' },
  { value: 'staff_visible',  label: 'Staff visible — CIP staff can also see this value' },
  { value: 'public',         label: 'Public — anyone with access can see this value' },
];

const inputStyle = {
  width: '100%',
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 6,
  color: '#e2e8f0',
  padding: '8px 12px',
  fontSize: 14,
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, color: '#94a3b8', marginBottom: 5, fontWeight: 600 };
const groupStyle = { marginBottom: 16 };

/**
 * LandValueForm — submission form for a new community land value.
 *
 * Supports:
 *   - Point: lat + lng fields
 *   - Polygon / LineString: paste raw GeoJSON coordinates
 *
 * Props:
 *   onSubmit  — (formData) => Promise<void>  (called with validated geometry)
 *   onCancel  — () => void
 */
function LandValueForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name:          '',
    category:      'cultural_heritage',
    description:   '',
    significance:  '',
    visibility:    'community_only',
    tags:          '',
    evidenceNotes: '',
    geoType:       'Point',
    lat:           '',
    lng:           '',
    geojsonText:   '',
  });
  const [saving,    setSaving]    = useState(false);
  const [error,      setError]     = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError,   setGpsError]   = useState('');  

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({ ...f, lat: String(pos.coords.latitude), lng: String(pos.coords.longitude) }));
        setGpsLoading(false);
      },
      () => {
        setGpsError('Could not get your location. Try picking on the map instead.');
        setGpsLoading(false);
      },
      { timeout: 10000 }
    );
  }

  function handlePickerConfirm(lat, lng) {
    setForm(f => ({ ...f, lat: String(lat), lng: String(lng) }));
  }

  const buildGeometry = () => {
    if (form.geoType === 'Point') {
      const lat = parseFloat(form.lat);
      const lng = parseFloat(form.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { type: 'Point', coordinates: [lng, lat] };
    }
    try {
      const coords = JSON.parse(form.geojsonText);
      return { type: form.geoType, coordinates: coords };
    } catch {
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim())     { setError('Name is required.'); return; }
    if (!form.category)        { setError('Category is required.'); return; }
    const geometry = buildGeometry();
    if (!geometry) {
      setError(
        form.geoType === 'Point'
          ? 'Valid latitude and longitude are required.'
          : 'Invalid GeoJSON coordinates. Check the format.'
      );
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSubmit({
        name:          form.name.trim(),
        category:      form.category,
        description:   form.description.trim(),
        significance:  form.significance.trim(),
        visibility:    form.visibility,
        tags:          form.tags.split(',').map(t => t.trim()).filter(Boolean),
        evidenceNotes: form.evidenceNotes.trim(),
        geometry,
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to submit land value.');
      setSaving(false);
    }
  };

  const selectedCat = CATEGORIES.find(c => c.value === form.category);

  return (
    <div>
      <div style={groupStyle}>
        <div style={labelStyle}>Name *</div>
        <input value={form.name} onChange={set('name')}
          placeholder="e.g. Salmon Spawning Ground at Red River" style={inputStyle} />
      </div>

      <div style={groupStyle}>
        <div style={labelStyle}>Category *</div>
        <select value={form.category} onChange={set('category')} style={inputStyle}>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        {selectedCat && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{selectedCat.hint}</div>
        )}
      </div>

      <div style={groupStyle}>
        <div style={labelStyle}>Description</div>
        <textarea rows={2} value={form.description} onChange={set('description')}
          placeholder="Brief description of this area" style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      <div style={groupStyle}>
        <div style={labelStyle}>Significance</div>
        <textarea rows={3} value={form.significance} onChange={set('significance')}
          placeholder="Why is this area significant to the community? Cultural, ecological, spiritual reasons…"
          style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      {/* Geometry */}
      <div style={{ background: '#0f172a', borderRadius: 8, padding: 14, marginBottom: 16,
        border: '1px solid #1e293b' }}>
        <div style={labelStyle}>Geometry *</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {['Point', 'Polygon', 'LineString'].map(t => (
            <button key={t} onClick={() => setForm(f => ({ ...f, geoType: t }))}
              style={{
                background: form.geoType === t ? '#0369a1' : 'transparent',
                border: `1px solid ${form.geoType === t ? '#0369a1' : '#334155'}`,
                color: form.geoType === t ? '#fff' : '#94a3b8',
                borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12,
              }}>
              {t}
            </button>
          ))}
        </div>

        {form.geoType === 'Point' ? (
          <div>
            {/* GPS + map picker buttons */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <button type="button" onClick={handleUseMyLocation} disabled={gpsLoading}
                style={{
                  padding: '6px 14px', background: gpsLoading ? '#475569' : '#0ea5e9',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 12,
                  fontWeight: 600, cursor: gpsLoading ? 'not-allowed' : 'pointer',
                }}>
                {gpsLoading ? '⏳ Locating…' : '📍 Use My Location'}
              </button>
              <button type="button" onClick={() => setPickerOpen(true)}
                style={{
                  padding: '6px 14px', background: '#16a34a',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 12,
                  fontWeight: 600, cursor: 'pointer',
                }}>
                🗺 Pick on Map
              </button>
            </div>

            {gpsError && (
              <div style={{ fontSize: 12, color: '#fca5a5', marginBottom: 8 }}>{gpsError}</div>
            )}

            {/* Coordinate summary badge */}
            {form.lat && form.lng && (
              <div style={{
                background: '#14532d', border: '1px solid #166534',
                borderRadius: 6, padding: '6px 12px', marginBottom: 10,
                fontSize: 12, fontWeight: 600, color: '#86efac',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>📍 {parseFloat(form.lat).toFixed(5)}, {parseFloat(form.lng).toFixed(5)}</span>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, lat: '', lng: '' }))}
                  style={{ background: 'none', border: 'none', color: '#86efac',
                    cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '0 2px' }}
                  title="Clear coordinates">✕</button>
              </div>
            )}

            <div style={{ fontSize: 11, color: '#475569', marginBottom: 6 }}>Or enter manually:</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>Latitude</div>
                <input type="number" value={form.lat} onChange={set('lat')}
                  placeholder="56.1234" step="any" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>Longitude</div>
                <input type="number" value={form.lng} onChange={set('lng')}
                  placeholder="-120.5678" step="any" style={inputStyle} />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div style={labelStyle}>
              GeoJSON coordinates array — {form.geoType === 'Polygon'
                ? '[[lng,lat],[lng,lat],…,[lng,lat]] (first = last to close the ring)'
                : '[[lng,lat],[lng,lat],…]'}
            </div>
            <textarea rows={4} value={form.geojsonText} onChange={set('geojsonText')}
              placeholder={form.geoType === 'Polygon'
                ? '[[-120.5, 56.1], [-120.4, 56.1], [-120.4, 56.2], [-120.5, 56.1]]'
                : '[[-120.5, 56.1], [-120.4, 56.15], [-120.3, 56.2]]'}
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
              {form.geoType === 'Polygon'
                ? 'Outer ring only. Paste GeoJSON-style [lng, lat] pairs. Polygon drawing tool coming in a later release.'
                : 'Paste GeoJSON-style [lng, lat] pairs.'}
            </div>
          </div>
        )}
      </div>

      {/* Map picker modal */}
      {pickerOpen && (
        <CoordPickerModal
          initLat={form.lat}
          initLng={form.lng}
          onConfirm={handlePickerConfirm}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <div style={groupStyle}>
        <div style={labelStyle}>Visibility</div>
        <select value={form.visibility} onChange={set('visibility')} style={inputStyle}>
          {VISIBILITY.map(v => (
            <option key={v.value} value={v.value}>{v.label}</option>
          ))}
        </select>
      </div>

      <div style={groupStyle}>
        <div style={labelStyle}>Evidence / Supporting Notes</div>
        <textarea rows={2} value={form.evidenceNotes} onChange={set('evidenceNotes')}
          placeholder="Any supporting evidence, oral history references, or documentation notes"
          style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      <div style={groupStyle}>
        <div style={labelStyle}>Tags (comma-separated)</div>
        <input value={form.tags} onChange={set('tags')}
          placeholder="e.g. salmon, river, spring, Treaty 8" style={inputStyle} />
      </div>

      {error && (
        <div style={{ background: '#450a0a', border: '1px solid #991b1b',
          borderRadius: 6, padding: '8px 12px', color: '#fca5a5', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel}
          style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
            borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontSize: 13 }}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={saving}
          style={{ background: '#16a34a', border: 'none', color: '#fff',
            borderRadius: 7, padding: '8px 18px', cursor: saving ? 'wait' : 'pointer',
            fontWeight: 700, fontSize: 13, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Submitting…' : 'Submit Land Value'}
        </button>
      </div>
    </div>
  );
}

export { CATEGORIES };
export default LandValueForm;
